import { useRouter } from 'expo-router';
import { Flame, Snowflake, Thermometer, Wind, Zap } from 'lucide-react-native';
import { useEffect, useState } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';

import { OptionRow, PrimaryCTA } from '@/components/onboarding/Controls';
import { Eyebrow, H1, Screen, WhyLine } from '@/components/onboarding/Screen';
import { Fonts, Palette, Spacing } from '@/constants/theme';
import type { HvacType } from '@/db/types';
import { useOnboarding, type HeatingFuel } from '@/store/onboarding';

const OPTIONS: { v: HvacType; title: string; sub: string; Icon: typeof Flame }[] = [
  { v: 'furnace', title: 'Forced-air furnace + AC', sub: 'Central ducted system', Icon: Flame },
  { v: 'boiler', title: 'Boiler + radiators', sub: 'Hydronic heat', Icon: Thermometer },
  { v: 'heatpump', title: 'Heat pump', sub: 'Heats and cools', Icon: Wind },
  { v: 'mini', title: 'Mini-split system', sub: 'Ductless units', Icon: Snowflake },
  { v: 'unsure', title: 'Not sure', sub: "We'll help you find out", Icon: Zap },
];

const FUEL_OPTIONS: { v: HeatingFuel; label: string }[] = [
  { v: 'gas', label: 'Natural gas' },
  { v: 'electric', label: 'Electric' },
  { v: 'oil', label: 'Oil' },
  { v: 'propane', label: 'Propane' },
  { v: 'dual-fuel', label: 'Dual-fuel' },
  { v: 'unsure', label: 'Not sure' },
];

export default function HvacScreen() {
  const router = useRouter();
  const setHvac = useOnboarding((s) => s.setHvac);
  const setHeatingFuel = useOnboarding((s) => s.setHeatingFuel);
  const stored = useOnboarding((s) => s.hvacType);
  const storedFuel = useOnboarding((s) => s.heatingFuel);
  // Default to forced-air furnace + AC and natural gas — the most common
  // US setup (roughly 60% of homes). Pre-selecting the modal answer keeps
  // the typical user moving and the atypical ones still see all options.
  const [picked, setPicked] = useState<HvacType | null>(stored ?? 'furnace');
  const [fuel, setFuel] = useState<HeatingFuel | null>(storedFuel ?? 'gas');
  useEffect(() => {
    if (!stored) setHvac('furnace');
    if (!storedFuel) setHeatingFuel('gas');
  }, [stored, storedFuel, setHvac, setHeatingFuel]);

  const pickHvac = (v: HvacType) => {
    setPicked(v);
    setHvac(v);
    // Heat pumps and electric mini-splits don't burn fuel — auto-pick
    // 'electric' and skip the user picking it themselves.
    if (v === 'heatpump' || v === 'mini') {
      setFuel('electric');
      setHeatingFuel('electric');
    }
  };

  const pickFuel = (f: HeatingFuel) => {
    setFuel(f);
    setHeatingFuel(f);
  };

  const next = () => router.push('/(onboarding)/inside' as never);

  return (
    <Screen
      scroll
      footer={<PrimaryCTA label="Continue" disabled={!picked || !fuel} onPress={next} />}>
      <Eyebrow>5 · Your home</Eyebrow>
      <H1>How is it heated{'\n'}& cooled?</H1>
      <View style={{ marginTop: Spacing.five, gap: Spacing.two }}>
        {OPTIONS.map(({ v, title, sub, Icon }) => (
          <OptionRow
            key={v}
            icon={
              <Icon
                size={22}
                color={picked === v ? Palette.cream : Palette.forest}
                strokeWidth={1.7}
              />
            }
            title={title}
            subtitle={sub}
            selected={picked === v}
            onPress={() => pickHvac(v)}
          />
        ))}
      </View>

      {/*
        Heating-fuel chip row. Disambiguates oil-tank / propane-regulator /
        gas-leak tasks downstream — the same furnace can run on any of
        four fuels and each unlocks a different maintenance bundle.
      */}
      <Text style={styles.fuelLabel}>WHAT FUEL HEATS IT?</Text>
      <View style={styles.fuelRow}>
        {FUEL_OPTIONS.map((opt) => {
          const sel = fuel === opt.v;
          return (
            <Pressable
              key={opt.v}
              onPress={() => pickFuel(opt.v)}
              style={({ pressed }) => [
                styles.chip,
                sel && styles.chipSel,
                pressed && { opacity: 0.85 },
              ]}>
              <Text style={[styles.chipText, sel && styles.chipTextSel]}>
                {opt.label}
              </Text>
            </Pressable>
          );
        })}
      </View>
      <WhyLine>
        Fuel determines which maintenance tasks fire — oil tanks need
        annual inspections, propane regulators every 7 years, gas lines
        get leak checks. Heat pumps and mini-splits are electric.
      </WhyLine>

    </Screen>
  );
}

const styles = StyleSheet.create({
  fuelLabel: {
    marginTop: Spacing.five,
    fontSize: 12,
    fontFamily: Fonts.sansBold,
    color: Palette.inkFaint,
    letterSpacing: 0.8,
    marginBottom: 8,
  },
  fuelRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  chip: {
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 999,
    borderWidth: 1.5,
    borderColor: Palette.line,
    backgroundColor: Palette.card,
  },
  chipSel: {
    backgroundColor: Palette.forest,
    borderColor: Palette.forest,
  },
  chipText: {
    fontFamily: Fonts.sansBold,
    fontSize: 13,
    color: Palette.ink,
    letterSpacing: 0.2,
  },
  chipTextSel: {
    color: Palette.cream,
  },
});
